import React, { useState } from 'react'
import { Calendar, momentLocalizer } from 'react-big-calendar'
import moment from 'moment'
import styled from 'styled-components'
import { colors } from '../../../Shared/colors'
import "react-big-calendar/lib/css/react-big-calendar.css";
import { useGetCalenderQuery } from '../../../features/leadsSlice'

const localizer = momentLocalizer(moment)

export const CalendarFirst = () => {
  const [view, setView] = useState('month')
  const [date, setDate] = useState(new Date())
  
  const { data: calender, isLoading } = useGetCalenderQuery()

  // Drag and drop handlers
  const handleDragStart = (event) => {
    // Handle drag start if needed
    // Currently disabled for calendar events
  }

  const handleDragOver = (event) => {
    // Handle drag over if needed
    event.preventDefault()
  }

  const handleDrop = (event) => {
    // Handle drop if needed
    // Currently disabled for calendar events
  }

  const myEventsList = []
  calender?.forEach(cal => {
    if (cal.nextTask?.startDate && cal.nextTask?.deadline) {
      const startDate = new Date(cal.nextTask?.startDate)
      const endDate = new Date(cal.nextTask?.deadline)
      
      // Only add events with valid dates
      if (!isNaN(startDate.getTime()) && !isNaN(endDate.getTime())) {
        myEventsList.push({
          'title': cal.nextTask?.nextTaskType || 'Task',
          'Lead ID': cal?.leadID?.leadID,
          'start': startDate,
          'end': endDate,
          'resource': {
            leadId: cal?.leadID?.leadID,
            taskType: cal.nextTask?.nextTaskType
          }
        })
      }
    }
  });

  const CustomEvent = ({ event }) => (
    <div style={{ 
      fontSize: '0.7rem', 
      fontWeight: '500', 
      color: '#1976d2',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      width: '100%'
    }}>
      {event.title} #{event['Lead ID']}
    </div>
  );

  const CustomToolbar = ({ label, onNavigate, onView }) => (
    <ToolbarContainer>
      <ToolbarLeft>
        <NavigationButton onClick={() => onNavigate('PREV')}>
          <span>‹</span>
        </NavigationButton>
        <NavigationButton onClick={() => onNavigate('NEXT')}>
          <span>›</span>
        </NavigationButton>
        <TodayButton onClick={() => onNavigate('TODAY')}>
          Today
        </TodayButton>
      </ToolbarLeft>
      
      <ToolbarCenter>
        <MonthLabel>{label}</MonthLabel>
      </ToolbarCenter>
      
      <ToolbarRight>
        <ViewButton 
          active={view === 'month'} 
          onClick={() => onView('month')}
        >
          Month
        </ViewButton>
        <ViewButton 
          active={view === 'week'} 
          onClick={() => onView('week')}
        >
          Week
        </ViewButton>
        <ViewButton 
          active={view === 'day'} 
          onClick={() => onView('day')}
        >
          Day
        </ViewButton>
        <ViewButton 
          active={view === 'agenda'} 
          onClick={() => onView('agenda')}
        >
          Agenda
        </ViewButton>
      </ToolbarRight>
    </ToolbarContainer>
  );

  if (isLoading) {
    return (
      <Main>
        <LoadingContainer>
          <LoadingSpinner />
          <LoadingText>Loading calendar events...</LoadingText>
        </LoadingContainer>
      </Main>
    )
  }

  return (
    <Main>
      <CalendarHeader>
        <HeaderTitle>
          <CalendarIcon>📅</CalendarIcon>
          Task Calendar
        </HeaderTitle>
        <HeaderSubtitle>Manage your tasks and deadlines</HeaderSubtitle>
      </CalendarHeader>
      
      <CalendarContainer>
        <Calendar
          localizer={localizer}
          events={myEventsList}
          step={60}
          date={date}
          onNavigate={setDate}
          view={view}
          onView={setView}
          defaultDate={new Date()}
          startAccessor="start"
          endAccessor="end"
          popup={true}
          components={{
            event: CustomEvent,
            toolbar: CustomToolbar,
          }}
          views={['month', 'week', 'day', 'agenda']}
          showMultiDayTimes
          popupOffset={{ x: 10, y: 10 }}
          onDragStart={handleDragStart}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          draggableAccessor={() => false}
        />
      </CalendarContainer>
    </Main>
  )
}

const Main = styled.div`
  width: 100%;
  height: calc(100vh - 10vh);
  padding: 1rem;
  background: #f8f9fa;
  border-radius: 8px;
  overflow: hidden;
`;

const CalendarHeader = styled.div`
  padding: 1rem;
  border-bottom: 1px solid #e0e0e0;
  background: white;
  margin-bottom: 0.5rem;
`;

const HeaderTitle = styled.h2`
  color: #333;
  font-size: 1.25rem;
  font-weight: 600;
  margin: 0;
  line-height: 1.2;
`;

const CalendarIcon = styled.span`
  font-size: 1.25rem;
  color: #666;
`;

const HeaderSubtitle = styled.p`
  color: #666;
  font-size: 0.85rem;
  margin: 0.25rem 0 0 0;
  font-weight: 400;
`;

const CalendarContainer = styled.div`
  height: calc(100% - 100px);
  padding: 0;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  
  /* Custom Toolbar Styles */
  .rbc-toolbar {
    display: none; /* Hide default toolbar, we use custom */
  }
  
  /* Month View Styles */
  .rbc-month-view {
    border: none;
    border-radius: 8px;
    overflow: hidden;
    background: white;
  }
  
  .rbc-header {
    background: #f5f5f5;
    color: #333;
    font-weight: 600;
    font-size: 0.9rem;
    padding: 12px 8px;
    text-align: center;
    border: none;
    border-bottom: 1px solid #e0e0e0;
    min-height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  
  .rbc-date-cell {
    padding: 8px;
    font-weight: 500;
    color: #333;
    font-size: 0.9rem;
    min-height: 35px;
    display: flex;
    align-items: center;
  }
  
  .rbc-off-range-bg {
    background: #fafafa;
  }
  
  .rbc-today {
    background: #e3f2fd;
  }
  
  .rbc-today .rbc-date-cell {
    color: #1976d2;
    font-weight: 700;
  }
  
  .rbc-month-row {
    min-height: 100px;
  }
  
  .rbc-date-cell > a {
    color: inherit;
    text-decoration: none;
  }
  
  /* Week View Styles */
  .rbc-time-view {
    border: none;
    border-radius: 12px;
    overflow: hidden;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    background: white;
  }
  
  .rbc-time-header {
    background: linear-gradient(135deg, ${colors.brown} 0%, #1a6b6b 100%);
    color: white;
    border: none;
    min-height: 50px;
  }
  
  .rbc-time-header-content {
    border: none;
  }
  
  .rbc-time-content {
    border: none;
  }
  
  .rbc-time-gutter {
    background: #f8f9fa;
    border-right: 1px solid ${colors.inputBorderWhite};
    min-width: 60px;
  }
  
  .rbc-timeslot-group {
    border-bottom: 1px solid ${colors.inputBorderWhite};
    min-height: 60px;
  }
  
  .rbc-day-slot {
    border-right: 1px solid ${colors.inputBorderWhite};
    min-height: 60px;
  }
  
  .rbc-time-slot {
    border-top: 1px solid ${colors.inputBorderWhite};
    min-height: 60px;
  }
  
  /* Day View Styles */
  .rbc-time-slot {
    border-top: 1px solid ${colors.inputBorderWhite};
    min-height: 60px;
  }
  
  /* Event Styles - Clean and Readable */
  .rbc-event {
    border: none !important;
    border-radius: 4px !important;
    padding: 3px 6px !important;
    margin: 1px 2px !important;
    font-size: 0.7rem !important;
    font-weight: 500 !important;
    overflow: hidden !important;
    white-space: nowrap !important;
    text-overflow: ellipsis !important;
    line-height: 1.1 !important;
    min-height: 20px !important;
    display: flex !important;
    align-items: center !important;
    background: #e3f2fd !important;
    color: #1976d2 !important;
    border-left: 3px solid #1976d2 !important;
  }
  
  .rbc-event-content {
    overflow: hidden !important;
    white-space: nowrap !important;
    text-overflow: ellipsis !important;
    width: 100% !important;
  }
  
  /* Agenda View Styles */
  .rbc-agenda-view {
    background: white;
    border-radius: 12px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    padding: 1.5rem;
    border: 1px solid ${colors.inputBorderWhite};
  }
  
  .rbc-agenda-view table {
    width: 100%;
    border-collapse: collapse;
    margin-top: 1rem;
    background-color: white;
    border-radius: 8px;
    overflow: hidden;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  }
  
  .rbc-agenda-view th {
    background: linear-gradient(135deg, ${colors.brown} 0%, #1a6b6b 100%);
    color: white;
    padding: 12px 16px;
    font-size: 0.85rem;
    text-align: left;
    text-transform: uppercase;
    font-weight: 600;
    letter-spacing: 0.5px;
    border: none;
  }
  
  .rbc-agenda-view tbody tr {
    border-bottom: 1px solid ${colors.inputBorderWhite};
    transition: all 0.2s ease;
  }
  
  .rbc-agenda-view tbody tr:hover {
    background: linear-gradient(135deg, rgba(36, 147, 147, 0.05) 0%, rgba(26, 107, 107, 0.05) 100%);
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }
  
  .rbc-agenda-view td {
    padding: 16px;
    font-size: 0.9rem;
    color: ${colors.text};
    vertical-align: top;
    border: none;
  }
  
  .rbc-agenda-view td:first-child {
    font-weight: 600;
    color: ${colors.brown};
    background: rgba(36, 147, 147, 0.05);
  }
  
  .rbc-agenda-view .no-events {
    text-align: center;
    color: ${colors.inputFeild};
    font-size: 1rem;
    font-style: italic;
    margin: 2rem 0;
    padding: 2rem;
  }
  
  /* Show More Button */
  .rbc-show-more {
    font-size: 0.7rem;
    color: ${colors.brown};
    text-decoration: none;
    cursor: pointer;
    padding: 2px 4px;
    border-radius: 3px;
    background: rgba(36, 147, 147, 0.1);
    transition: all 0.2s ease;
    
    &:hover {
      background: rgba(36, 147, 147, 0.2);
      color: ${colors.brown};
    }
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  gap: 1rem;
`;

const LoadingSpinner = styled.div`
  width: 40px;
  height: 40px;
  border: 3px solid rgba(36, 147, 147, 0.2);
  border-top: 3px solid ${colors.brown};
  border-radius: 50%;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const LoadingText = styled.p`
  color: ${colors.inputFeild};
  font-size: 1rem;
  margin: 0;
`;

/* Custom Toolbar Components */
const ToolbarContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 1rem;
  background: white;
  border-bottom: 1px solid #e0e0e0;
`;

const ToolbarLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const ToolbarCenter = styled.div`
  flex: 1;
  text-align: center;
`;

const ToolbarRight = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const NavigationButton = styled.button`
  width: 32px;
  height: 32px;
  border: 1px solid #e0e0e0;
  background: #f5f5f5;
  color: #333;
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1rem;
  font-weight: 500;
  transition: all 0.2s ease;
  
  &:hover {
    background: #e0e0e0;
    border-color: #ccc;
  }
`;

const TodayButton = styled.button`
  padding: 6px 12px;
  border: none;
  background: #1976d2;
  color: white;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.85rem;
  font-weight: 500;
  transition: all 0.2s ease;
  
  &:hover {
    background: #1565c0;
  }
`;

const MonthLabel = styled.h3`
  font-size: 1.1rem;
  font-weight: 600;
  color: #333;
  margin: 0;
`;

const ViewButton = styled.button`
  padding: 6px 12px;
  border: 1px solid #e0e0e0;
  background: ${props => props.active ? '#1976d2' : 'white'};
  color: ${props => props.active ? 'white' : '#333'};
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.8rem;
  font-weight: 500;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${props => props.active ? '#1565c0' : '#f5f5f5'};
    border-color: #1976d2;
  }
`;

/* Event Components - Using inline styles for simplicity */

